import {
  GROUP_EXPENSE_LIMIT_ERROR_KEYS,
  LINE_ITEMS_ERROR_KEYS,
  RECURRING_ERROR_KEYS,
  SYNC_REPEAT_PRO_REQUIRED_ERROR,
} from '@splitfiesta/shared';

export { SYNC_REPEAT_PRO_REQUIRED_ERROR };

/** `lastError` written when no exchange rate exists for a queued expense. */
export const SYNC_RATE_UNAVAILABLE_ERROR =
  'expenses:conversion.syncUnavailable';

/** `lastError` written when the queued expense's group is gone for this user. */
export const SYNC_GROUP_GONE_ERROR = 'common:offline.groupGone';

/**
 * The API's `code` is only the category (`NOT_FOUND` / `FORBIDDEN`), shared
 * with every other 404/403, so the translation key is what names the group.
 */
const GROUP_GONE_KEYS: Record<number, string> = {
  404: 'errors:group.notFound',
  403: 'errors:group.notMember',
};

/** The group was deleted, or the user is no longer one of its members. */
export function isGroupGoneSyncError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { status, translationKey } = error as {
    status?: number;
    translationKey?: string;
  };
  return (
    typeof status === 'number' &&
    translationKey !== undefined &&
    GROUP_GONE_KEYS[status] === translationKey
  );
}

/** `lastError` written when split by item is no longer allowed for the queued expense's group. */
export const SYNC_LINE_ITEMS_NOT_ALLOWED_ERROR =
  'expenses:lineItems.syncNotAllowed';

/** API translation keys that no retry can fix, mapped to the `lastError` the indicators render. */
const PERMANENT_BY_TRANSLATION_KEY: Record<string, string> = {
  [LINE_ITEMS_ERROR_KEYS.notAllowed]: SYNC_LINE_ITEMS_NOT_ALLOWED_ERROR,
  [RECURRING_ERROR_KEYS.proRequired]: SYNC_REPEAT_PRO_REQUIRED_ERROR,
};

/** `lastError` written when the queued expense's group is at the free expense limit. */
export const SYNC_GROUP_LIMIT_ERROR = 'common:offline.groupLimitReached';

/**
 * API keys that fail the row with a named message but may pass on a later retry.
 * The limit lifts once the group goes Pro or loses expenses, so unlike a
 * permanent refusal (which spends the whole budget at once) the row spends its
 * budget like any 4xx; `requeueGroupLimitFailures` gives it a fresh one when
 * the group goes Pro.
 */
const RETRYABLE_BY_TRANSLATION_KEY: Record<string, string> = {
  [GROUP_EXPENSE_LIMIT_ERROR_KEYS.reached]: SYNC_GROUP_LIMIT_ERROR,
};

function lastErrorFrom(
  table: Record<string, string>,
  error: unknown
): string | null {
  if (!error || typeof error !== 'object') return null;
  const { translationKey } = error as { translationKey?: unknown };
  if (typeof translationKey !== 'string') return null;
  return Object.prototype.hasOwnProperty.call(table, translationKey)
    ? table[translationKey]
    : null;
}

/** The `lastError` for a permanent failure, or null when the error may still succeed on a retry. */
export function permanentSyncErrorFor(error: unknown): string | null {
  return lastErrorFrom(PERMANENT_BY_TRANSLATION_KEY, error);
}

/** The named `lastError` for a failure a later retry may clear, or null. */
export function retryableSyncErrorFor(error: unknown): string | null {
  return lastErrorFrom(RETRYABLE_BY_TRANSLATION_KEY, error);
}
